import React, { useCallback, memo } from 'react';
import { RenderRawHTML, useQueryFetch } from '@ace/react-utilities';
import { useSMARTGoalPageState, useSMARTGoalAppProps } from 'context/SMARTGoalAppContext';
import { queryProps, useSMARTGoalStepState } from 'Components/SMARTGoal/SMARTGoalHelper';
import styles from 'assets/css/SMARTGoal.module.css';



const PageContent = () => {
    const { showWelcome, showStep, showReview } = useSMARTGoalPageState();

    return (
        <div>
            {showWelcome && <PageContentWelcome />}
            {showStep && <PageContentDynamic />}
            {showReview && <PageContentReview />}
        </div>
    )

}

export default PageContent;

const PageContentTemplate = ({ preTitle, title, subTitle, children }) => (
    <div className={styles["sg-content__container"]}>
        <div className={styles["sg-content__header-container"]}>
            <h3>{preTitle}</h3>
            <h2>{title}</h2>
            <h4>{subTitle}</h4>
        </div>
        <div className={styles["sg-content__body-container"]}>
            {children}
        </div>
    </div>
);

const PageContentWelcome = memo(() => (
    <PageContentTemplate {...{ preTitle: 'Specific. Measureable. Achievable. Relevant. Time-bound.', title: 'Welcome to SMART goals'  }}>
        <p>During your career one of the skills you'll employ is leading your clients through SMART goal setting. This tool will guide you through the process of setting SMART goals for your own education path.</p>
        <h4>What are SMART goals?</h4>
        <p>You'll learn about SMART goals in depth during your program. In short, SMART goals are specific, measurable, attainable, realistic, and time-bound. Let's get started setting your goals for each component of this program.</p>
        <p>As you set these goals, keep in mind that it is realistic to complete the entire program in eight to 12 months. In fact, we recommend that you do so. Also, keep in mind that that each course must be started within 18 months of purchase (see Terms and Conditions for more details).</p>
        <h4>Are my SMART goals flexible?</h4>
        <p>As is common with goal setting, you may find that you need to adjust your goals in the future as you get a more complete vision of what the program entails. Keep in mind that when you adjust one goal, you may need to adjust subsequent goals, as well. You may also decide that you aren't quite ready to complete this exercise in its entirety at the moment. That's not a problem. Come back any time to adjust your goals or complete the full exercise.</p>
        <p>This tool will help you set specific, measurable, and time-bound goals. While we include information about what is realistic for many students, you'll need to consider what is realistic and attainable based on your personal circumstances. To do so, consider:
            <ul>
                <li>How many hours each week will you realistically be able to devote to your studies?</li>
                <li>What study habits have worked well for you in the past?</li>
            </ul>
        </p>
        <p>Let's get started.</p>
    </PageContentTemplate>
));

const PageContentReview = memo(() => { 
    const { maxSteps } = useSMARTGoalAppProps();
    return (
        <PageContentTemplate {...{ preTitle: `Step ${(maxSteps + 1)} of ${(maxSteps + 1)}`, title: 'Review your SMART goals' }}>
            <p>As is common with goal setting, you may find that you need to adjust your goals.  Adjust your goals below to set dates that are Realistic.</p>
            <p>Note: Keep in mind that when you adjust one goal, you'll want to adjust the subsequent goals as well!</p>
        </PageContentTemplate>
    );
});

const PageContentDynamic = () => {
    const { maxSteps } = useSMARTGoalAppProps();
    const { currentStepKey } = useSMARTGoalStepState();
    const { data: page } = useQueryFetch(queryProps.appSteps.componentName, queryProps.appSteps.retrievalUrl, { 
        select: useCallback((data) => (data.PagesByKey[currentStepKey]), [currentStepKey])
    });

    const {
        EstimatedTime,
        PageContent,
        PageTitle,
        Title,
        Step
    } = page;

    const editorProps = {
        preTitle: `Step ${Step} of ${(maxSteps + 1)}`,
        title: Title,
        subTitle: `Estimated time to complete: ${EstimatedTime}`
    }

    return (
        <PageContentTemplate {...editorProps}>
            {RenderRawHTML(PageContent)}
        </PageContentTemplate>
    )
}